import { httpsCallable, getFunctions } from 'firebase/functions';
import { ProfileRole } from '../ts/enums';
import ConfigCache from './ConfigCache';
import Settings from '../controllers/settings';
import { ICategoryItem, ISettingsItem, ITaskItem, ITaskVoteConfig, ILockPayload, ILockReceipt, PilloryTopic, IProofPayload, VoteTask } from '../ts/Definitions';
import { convertToCountTypeString, getItemTypeDisplayName, isCategoryLimited } from './utils';
import StatefulSettings from '../controllers/StatefulSettings';
import BaseSettings from '../controllers/BaseSettings';
import { formatValidDate } from './dateUtils';

const shouldFireTicker = () => import.meta.env.PROD;

export const sendNewUserDiscordTicker = (config: ConfigCache, role: ProfileRole, username: string) => {
    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    const message = `${username} has joined Tarlings (${ProfileRole[role]})`
    void discordMessage({
        method: 'sendNewMemberTicker',
        message,
        username,
        ownername: '',
    });
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const canHandleDiscordCalls = (_config: ConfigCache, _username: string) => {
    return true; // let them all through
}

export const sendFailedMessage = (settings: Settings, failedText: string) => {
    if (canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        if (shouldFireTicker() && !settings.getGeneralSettings().disableDiscordShaming) {
            void discordMessage({
                method: 'sendFailedTaskMessage',
                message: failedText
            });
        }
    }
}

export const handleStartingTask = async (settings: StatefulSettings, item: ISettingsItem, justAddedTask: boolean, isTimed: boolean) => {
    if (!canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)) return;
    if (shouldFireTicker() && !settings.getGeneralSettings().disableDiscordTicker) {
        const catItem = (item as ICategoryItem);
        const basketCategories = settings.hasCategoriesInBasket(false, catItem.categories, item.key);
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        let message = `${settings.getControl().username} has started ${getItemTypeDisplayName(item.itemType)} ${item.name}`

        if (basketCategories) {
            const config = await settings.getConfig().fetchConfig();
            const matches = isCategoryLimited(basketCategories, config.ignoreDiscordBotCategories);
            if (!matches || matches.length !== basketCategories.length) {
                message = message + `\n:warning: task started with other similar tasks (${basketCategories.join()}). check tasks running are valid together and ${settings.getControl().username} not cheating.`;
            }
        }
        if (justAddedTask === true && isTimed) {
            message = message + '\n:question: task started with very quickly after another timed task. These tasks may be able to run concurrently, but worth checking for cheating';
        }
        void discordMessage({
            method: 'sendTickerDiscordMessage',
            message,
            username: settings.getControl().username,
            ownername: settings.getControl().ownerid || settings.getControl().loanedOwnerid ? settings.getControl().getOwner().username : '',
        });
    }
}

export const handleAttendanceRequest = (settings: StatefulSettings, duration: number) => {
    if (!canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)) return;
    if (shouldFireTicker() && !settings.getGeneralSettings().disableDiscordTicker) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        const message = `${settings.getControl().username} has been requested to attend their owner ${settings.getControl().getOwner().username}. They have ${duration} seconds to log on and acknowledge.  Failure to comply will result in penalty.`
        void discordMessage({
            method: 'sendAttendanceRequestDiscordMessage',
            message,
            username: settings.getControl().username,
            ownername: settings.getControl().ownerid || settings.getControl().loanedOwnerid ? settings.getControl().getOwner().username : '',
        });
    }
}

export const handleDiscordRequestProofTicker = (settings: StatefulSettings, proofText: string) => {
    if (canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        if (shouldFireTicker() && !settings.getGeneralSettings().disableDiscordProof) {
            void discordMessage({
                method: 'sendProofRequestMessage',
                message: proofText,
            });
        }
    }
}

export const handleDiscordPilloryTicker = (settings: Settings, topic: PilloryTopic) => {
    if (canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)
        && shouldFireTicker()
        && !settings.getGeneralSettings().disableDiscordTicker) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        void discordMessage({
            method: 'createPilloryTopic',
            message: `${topic.describe()}\n\nClick Here to vote:\n${window.location.protocol}//${window.location.hostname}/pillory?command=${topic.userid}`,
            username: topic.username,
            ownername: topic.ownername || '',
        });
    }
}

export const handleDiscordVoteTicker = (settings: Settings, topic: VoteTask) => {
    if (canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)
        && shouldFireTicker()
        && !settings.getGeneralSettings().disableDiscordTicker) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        void discordMessage({
            method: 'createPilloryTopic',
            message: `${topic.describe()}\n\nClick Here to vote:\n${window.location.protocol}//${window.location.hostname}/vote?command=${topic.userid}`,
            username: topic.username,
            ownername: settings.getControl().getOwner().username || '',
        });
    }
}


export const handleDiscordLovenseTicker = (settings: Settings, topic: VoteTask) => {
    if (canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)
        && shouldFireTicker()
        && !settings.getGeneralSettings().disableDiscordTicker) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        void discordMessage({
            method: 'sendTickerLovenseMessage',
            message: `${topic.username} has started a task that uses a lovense toy.\n\nClick Here to vote:\n${window.location.protocol}//${window.location.hostname}/vote?command=${topic.userid}`,
            username: topic.username,
            ownername: settings.getControl().getOwner().username || '',
        });
    }
}

export const handleDiscordOwnedItemsTicker = (settings: StatefulSettings, item: ITaskItem, owned: boolean, didOwn: boolean) => {

    if (canHandleDiscordCalls(settings.getConfig(), settings.getControl().username)) {
        let message = `${settings.getControl().username} has updated ${item.name} `;
        if (!didOwn && owned) message += 'to be owned. :smiling_imp:';
        if (didOwn && !owned) message += 'to be unowned. :face_holding_back_tears:';
        if (didOwn && owned && item.options && item.options.length > 0) message += `options to now include ${item.options.join()} :smiley:`;
        if (didOwn && owned && (!item.options || item.options.length === 0)) message += 'to have no options :pleading_face:';

        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        void discordMessage({
            method: 'sendOwnedItemsDiscordMessage',
            message,
            username: settings.getControl().username,
            ownername: settings.getControl().getOwner().username,
        });
    }
}

export const handleDiscordPilloryTickerUp = (settings: BaseSettings, pillory: PilloryTopic) => {
    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    void discordMessage({
        method: 'sendVoteDiscordMessage',
        message: `Pillory: ${settings.getControl().username} has voted to increase the count on ${pillory.username} punishment ${pillory.name} to ${convertToCountTypeString(pillory.getCurrentCount(), pillory.countType)}`,
        username: pillory.username,
        ownername: settings.getControl().getOwner().username,
    });
}

export const handleDiscordPilloryTickerDown = (settings: BaseSettings, pillory: PilloryTopic) => {
    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    void discordMessage({
        method: 'sendVoteDiscordMessage',
        message: `Pillory: ${settings.getControl().username} has voted to decrease the count on ${pillory.username} punishment ${pillory.name} to ${convertToCountTypeString(pillory.getCurrentCount(), pillory.countType)}`,
        username: pillory.username,
        ownername: settings.getControl().getOwner().username,
    });
}

export const handleDiscordVoteTickerUp = (settings: BaseSettings, vote: VoteTask) => {

    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    void discordMessage({
        method: 'sendVoteDiscordMessage',
        message: `Task Vote: ${settings.getControl().username} has voted to increase the time on ${vote.username} task ${vote.name} to ${formatValidDate(vote.getEndTime(), 'dd MMM yyyy HH:mm')}`,
        username: vote.username,
        ownername: settings.getControl().getOwner().username,
    });
}

export const handleDiscordVoteTickerRoulette = (settings: StatefulSettings, vote: VoteTask, config: ITaskVoteConfig) => {

    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    void discordMessage({
        method: 'sendVoteDiscordMessage',
        message: `Task Vote: ${settings.getControl().username} has ${config.isUp ? 'increased' : 'decreased'} the time on their task ${vote.name} by ${convertToCountTypeString(config.voteIncrement, config.countType)}`,
        username: vote.username,
        ownername: settings.getControl().getOwner().username,
    });
}

export const handleDiscordVoteTickerDown = (settings: BaseSettings, vote: VoteTask) => {

    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    void discordMessage({
        method: 'sendVoteDiscordMessage',
        message: `Task Vote: ${settings.getControl().username} has voted to decrease the time on ${vote.username} task ${vote.name} to ${formatValidDate(vote.getEndTime(), 'dd MMM yyyy HH:mm')}`,
        username: vote.username,
        ownername: settings.getControl().getOwner().username,
    });
}

export const handleDiscordPausedLockTicker = (settings: StatefulSettings, paused: boolean, receipt: ILockReceipt) => {
    const method = paused ? 'sendPauseMember' : 'sendUnlockMember';
    if (shouldFireTicker()) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        void discordMessage({
            ...receipt,
            method,
        });
    }
}

export const handleDiscordLockImage = (payload: ILockPayload) => {
    if (shouldFireTicker()) {
        const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
        void discordMessage(payload);
    }
}

export const handleDiscordProofRequest = (payload: IProofPayload) => {
    const discordMessage = httpsCallable(getFunctions(), 'handleMessage');
    void discordMessage(payload);
}